import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getAngebotsIds,
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  QuoteDbInitPayload,
  WrappedSavedStates
} from '@ps20-frontend/shop-integration-redux';
import { AnyAction } from 'redux';
import {
  AvSpeichernResponseModel,
  AvUpdateRequestModel,
  Dialogschritt,
  UpdateAvRequestModelForCap,
} from '@ps20/bff-models';


import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { IAppState } from '../localStore';
import { isSdwSession, shouldUseSdwEndpoint } from '../selectors/transientState.selector';
import { generateSpeichernRequest } from './speichern/speichernRequestMapper';
import { getQuoteDbDaten } from './doQuoteDBRequestSaga';

export enum SPEICHERN_REQUEST_EVENTS {
  SPEICHERN_REQUEST_SUCCESSFUL = 'SPEICHERN_REQUEST_SUCCESSFUL',
  SPEICHERN_REQUEST_FAILED = 'SPEICHERN_REQUEST_FAILED',
}

export function* watchSpeichernRequested(): SagaIterator {
  yield takeLatest(GlobalActions.SPEICHERN_REQUESTED, safeSaga(speichernSaga, App.SHOP));
  yield takeEvery(GlobalActions.SPEICHERDATEN_REQUESTED, safeSaga(speicherdatenSaga, App.SHOP));
  yield takeEvery(
    GlobalActions.SPEICHERDATEN_REQUESTED_CAP,
    safeSaga(speicherdatenSagaCap, App.SHOP),
  );
  yield takeEvery(
    SPEICHERN_REQUEST_EVENTS.SPEICHERN_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP),
  );
  yield takeEvery(
    SPEICHERN_REQUEST_EVENTS.SPEICHERN_REQUEST_FAILED,
    safeSaga(errorCallback, App.SHOP),
  );
}

function isNewVorgang(shopState: IAppState): boolean {
  return (
    !shopState.transientState.datenhaltung.av.cryptId ||
    shopState.transientState.datenhaltung.kdv.copyFlag === 1
  );
}

export function getUrlForSaveOrSaveWithUpdate(shopState: IAppState, saveAsCopy: boolean): string {
  if (isNewVorgang(shopState) || (isSdwSession(shopState) && saveAsCopy)) {
    return getEndpointUrl(shouldUseSdwEndpoint(shopState), '/privatschutz/api/save');
  }
  return getEndpointUrl(shouldUseSdwEndpoint(shopState), '/privatschutz/api/aktualisieren');
}

export function* speichernSaga(action?: AnyAction): SagaIterator {
  const shopState: IAppState = yield select();
  const angebotsIds: Array<AngebotsId> = yield call(getAngebotsIds);
  const savePayload = generateSpeicherdaten(shopState, angebotsIds, false, action?.payload?.forwardToUnderwriter);

  const globalEventDispatcher = GlobalEventDispatcher.getInstance();

  if (savePayload.avRequestsModel.kdv.metadaten.gesamtbeitrag > 0) {
    globalEventDispatcher.dispatch(
      new GlobalActions().vorschlagdruckdatenRequested(
        savePayload.avRequestsModel.kdv.metadaten.gesamtbeitrag,
      ),
    );
    const vorschlagdruckdaten = yield take(GlobalActions.VORSCHLAG_DRUCK_DATEN_GENERATED);
    savePayload.avRequestsModel.vorschlagsdruckModel = vorschlagdruckdaten.payload;
  }

  yield put(
    new GlobalActions().doRequest({
      appId: App.SHOP,
      body: savePayload.avRequestsModel,
      url: getUrlForSaveOrSaveWithUpdate(shopState, savePayload.saveAsCopy),
      successEvent: SPEICHERN_REQUEST_EVENTS.SPEICHERN_REQUEST_SUCCESSFUL,
      errorEvent: SPEICHERN_REQUEST_EVENTS.SPEICHERN_REQUEST_FAILED,
    }),
  );
}

export function successCallback(action: AnyAction): void {
  const payload: AvSpeichernResponseModel = action.payload;
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();

  globalEventDispatcher.dispatch(
    new GlobalActions().saveStatusReceived({
      cryptId: payload.CryptId,
      dokumentId: payload.DokumentId,
      status: 200,
      angebotsBuendelId: payload.AngebotsBuendelId,
      underReview: payload.UnderReview,
    }),
  );

  if (payload.CryptId) {
    PS20Logger.info(App.SHOP, 'Emitting OFFER_SAVED_WITH_CRYPTID event');
    globalEventDispatcher.dispatch(
      new GlobalActions().offerSavedWithCryptId({
        cryptId: payload.CryptId,
        angebotsBuendelId: payload.AngebotsBuendelId,
      }),
    );
  }
}

export function errorCallback(): void {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  globalEventDispatcher.dispatch(
    new GlobalActions().saveStatusReceived({ cryptId: '', status: 404 }),
  );
}

function generateSpeicherdaten(
  shopState: IAppState,
  angebotsIds: Array<AngebotsId>,
  keepGisDaten: boolean,
  forwardToUnderwriter?: boolean,
): { avRequestsModel: AvUpdateRequestModel; saveAsCopy: boolean } {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const dashboardState = globalEventDispatcher.getStateByAngebot(App.SHOP_DASHBOARD);
  const toolbarState = globalEventDispatcher.getStateByAngebot(App.SHOP_TOOLBAR);
  const checkoutState = globalEventDispatcher.getStateByAngebot(App.SHOP_CHECKOUT);
  const saveAsCopy = toolbarState.speichern.saveAsCopy;
  if (saveAsCopy) {
    delete shopState.transientState.datenhaltung.av.cryptId;
    checkoutState.dokumentId = null;
    toolbarState.speichern.saveAsCopy = false;
  }

  const appStates: WrappedSavedStates = angebotsIds.map(app => {
    const appState = globalEventDispatcher.getStateByAngebot(app.appId, app.angebotsId);
    return {
      appId: app.appId,
      angebotsId: app.angebotsId,
      state: appState,
    };
  });
  return {
    avRequestsModel: generateSpeichernRequest(
      shopState,
      dashboardState,
      checkoutState,
      toolbarState,
      appStates,
      keepGisDaten,
      forwardToUnderwriter,
    ),
    saveAsCopy,
  };
}

export function* speicherdatenSaga(): SagaIterator {
  const angebotsIds: Array<AngebotsId> = yield call(getAngebotsIds);
  const avUpdateRequest: AvUpdateRequestModel = generateSpeicherdaten(
    yield select(),
    angebotsIds,
    true,
  ).avRequestsModel;
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  globalEventDispatcher.dispatch(new GlobalActions().speicherdatenGenerated(avUpdateRequest));
}

export function* speicherdatenSagaCap(): SagaIterator {
  const angebotsIds: Array<AngebotsId> = yield call(getAngebotsIds);
  const shopState: IAppState = yield select();
  const avUpdateRequest: UpdateAvRequestModelForCap = generateSpeicherdaten(
    shopState,
    angebotsIds,
    true,
  ).avRequestsModel;


  const quoteDbInitAction: GlobalAction<QuoteDbInitPayload> = {
    type: '',
    payload: {
      dialogschritt: Dialogschritt.CHECKOUT_ANTRAG_EINREICHEN,
    },
  };

  avUpdateRequest.quoteDbRequest = yield call(getQuoteDbDaten, quoteDbInitAction, shopState);

  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  globalEventDispatcher.dispatch(new GlobalActions().speicherdatenGeneratedForCap(avUpdateRequest));
}
